/**
 * BetaApplicationDetail Component
 *
 * Administrative interface for viewing individual beta application details.
 * Provides comprehensive view of submitted application data with admin review context.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, TrendingUp, Target, Shield, UserCheck, Mail } from 'lucide-react';

import { AdminLayout } from '@/components/shared/layouts';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Chip,
  Spinner,
  Divider,
  Accordion,
  AccordionItem,
  useDisclosure,
  useToastNotification
} from '@enfomentis/ui';
import { betaApplicationService } from '@/services/BetaApplicationService';
import { AdminDecisionModal, type AdminDecisionData } from './AdminDecisionModal';
import { useAuth } from '@/hooks/useAuth';
import { Brand } from '@enfomentis/shared/config';
import type {
  BetaApplication,
  BetaApplicationStatus
} from '@/types/supabase/BetaApplication';

// ============================================================================
// Edge Function Types
// ============================================================================

/**
 * Response from admin-approve-application Edge Function (success)
 */
interface AdminApproveResponse {
  success: true;
  application: {
    id: string;
    status: string;
    reviewedBy: string | null;
    reviewedAt: string | null;
  };
  statusHistoryId: string;
  invitationTokenId?: string;
  invitationToken?: string;
  tokenExpiresAt?: string;
  emails: {
    notificationSent: boolean;
    notificationError?: string;
    invitationSent: boolean;
    invitationError?: string;
  };
}

/**
 * Response from admin-approve-application Edge Function (error)
 */
interface AdminApproveErrorResponse {
  success: false;
  error: string;
  code?: string;
}

/**
 * Minimal invitation token state for UI display
 * (Edge Function returns token string directly, not full InvitationToken object)
 */
interface InvitationTokenState {
  token: string;
  expiresAt: string;
  usedAt: string | null;
}

/**
 * Props for BetaApplicationDetail component
 */
export interface BetaApplicationDetailProps {
  /** Optional application ID override (for testing) */
  applicationId?: string;
}

/**
 * Beta Application Detail Component
 *
 * Features:
 * - Comprehensive view of individual beta application
 * - Organized sections for easy admin review
 * - Mobile-responsive design with proper spacing
 * - Loading states and error handling
 * - Foundation for Task 3E approval/rejection workflow
 * - Consistent styling with existing admin interface
 *
 * Usage:
 * ```tsx
 * // Via routing: /admin/beta-applications/:id
 * <BetaApplicationDetail />
 *
 * // Direct usage with ID:
 * <BetaApplicationDetail applicationId="123" />
 * ```
 */
export const BetaApplicationDetail: React.FC<BetaApplicationDetailProps> = ({
  applicationId: propApplicationId
}) => {
  const { id: routeApplicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const applicationId = propApplicationId || routeApplicationId;

  // Auth context for admin username and session token
  const { user, authState } = useAuth();
  const adminUsername = user?.email || 'Unknown Admin';

  // State management
  const [application, setApplication] = useState<BetaApplication | null>(null);
  const [invitationToken, setInvitationToken] = useState<InvitationTokenState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingToken, setLoadingToken] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmittingDecision, setIsSubmittingDecision] = useState<boolean>(false);
  const [isResendingInvitation, setIsResendingInvitation] = useState<boolean>(false);
  const [expandedAccordionKeys, setExpandedAccordionKeys] = useState<Set<string>>(new Set());

  // Modal state
  const { isOpen: isDecisionModalOpen, onOpen: openDecisionModal, onClose: closeDecisionModal } = useDisclosure();

  // Toast notifications
  const { showToast } = useToastNotification();

  // Ref to track token load attempts (prevents infinite loop on failure)
  const hasAttemptedTokenLoad = useRef<string | null>(null);

  // ============================================================================
  // Edge Function Helper
  // ============================================================================

  /**
   * Call admin-approve-application Edge Function
   *
   * @param appId - Beta application ID
   * @param decision - Decision status ('approved' | 'rejected' | 'waitlisted')
   * @param adminNotes - Optional admin notes
   * @param sendNotificationEmail - Whether to send notification email
   * @param sendInvitationEmail - Whether to send invitation email (approved only)
   * @returns Promise resolving to Edge Function response
   * @throws Error if request fails
   */
  const callAdminApproveEdgeFunction = useCallback(async (
    appId: string,
    decision: BetaApplicationStatus,
    adminNotes: string | undefined,
    sendNotificationEmail: boolean,
    sendInvitationEmail: boolean
  ): Promise<AdminApproveResponse> => {
    // Get session token from auth state
    if (!authState.sessionToken) {
      throw new Error('Not authenticated. Please log in again.');
    }

    const supabaseUrl = import.meta.env['VITE_SUPABASE_URL'];
    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured');
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/admin-approve-application`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authState.sessionToken}`,
        },
        body: JSON.stringify({
          applicationId: appId,
          decision,
          adminNotes: adminNotes || undefined,
          sendNotificationEmail,
          sendInvitationEmail,
        }),
      }
    );

    // Parse response
    const result = await response.json() as AdminApproveResponse | AdminApproveErrorResponse;

    if (!response.ok || !result.success) {
      const errorResult = result as AdminApproveErrorResponse;
      throw new Error(errorResult.error || `Request failed with status ${response.status}`);
    }

    return result as AdminApproveResponse;
  }, [authState.sessionToken]);

  /**
   * Load token details for approved applications (silent - no toasts)
   *
   * Calls Edge Function with no email flags to retrieve existing token.
   * This avoids RLS restrictions on invitation_tokens table.
   */
  const loadTokenDetails = useCallback(async (appId: string): Promise<void> => {
    // Guard: Skip if auth not ready (avoids predictable errors during page load)
    if (!authState.sessionToken) {
      return;
    }

    try {
      setLoadingToken(true);

      // Call Edge Function with no emails - just fetches existing token
      const result = await callAdminApproveEdgeFunction(
        appId,
        'approved', // Same status = idempotent, returns existing token
        undefined,
        false, // No notification email
        false  // No invitation email
      );

      // Update token state if token was returned
      if (result.invitationToken && result.tokenExpiresAt) {
        setInvitationToken({
          token: result.invitationToken,
          expiresAt: result.tokenExpiresAt,
          usedAt: null // Edge Function doesn't return usedAt, assume not used if token returned
        });
      }
    } catch (err) {
      // Silent failure - don't show toast, just log
      console.error('Failed to load token details:', err);
    } finally {
      setLoadingToken(false);
    }
  }, [authState.sessionToken, callAdminApproveEdgeFunction]);

  // ============================================================================
  // Data Loading
  // ============================================================================

  // Load application data
  const loadApplication = useCallback(async (): Promise<void> => {
    if (!applicationId) {
      setError('No application ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const applicationData = await betaApplicationService.get(applicationId);
      setApplication(applicationData);

      // Token details loaded separately via loadTokenDetails() after this completes
      // (called in useEffect when status is 'approved')
    } catch (err) {
      console.error('Failed to load beta application:', err);
      setError('Failed to load application details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  // Resend invitation email handler
  const handleResendInvitation = useCallback(async (): Promise<void> => {
    if (!application) return;

    try {
      setIsResendingInvitation(true);

      // Call Edge Function to resend invitation email
      // Same status (approved) = idempotent, just sends email
      const result = await callAdminApproveEdgeFunction(
        application.id,
        'approved',
        undefined,
        false, // No notification email
        true   // Send invitation email
      );

      // Update token state from response
      if (result.invitationToken && result.tokenExpiresAt) {
        setInvitationToken({
          token: result.invitationToken,
          expiresAt: result.tokenExpiresAt,
          usedAt: null
        });
      }

      // Show result toast
      if (result.emails.invitationSent) {
        showToast({
          color: 'success',
          title: 'Invitation Sent',
          description: `Invitation email sent to ${application.email}`
        });
      } else if (result.emails.invitationError) {
        showToast({
          color: 'warning',
          title: 'Email Failed',
          description: result.emails.invitationError
        });
      }
    } catch (err) {
      console.error('Failed to resend invitation:', err);
      showToast({
        color: 'danger',
        title: 'Failed to Send',
        description: err instanceof Error ? err.message : 'Failed to send invitation email. Please try again.'
      });
    } finally {
      setIsResendingInvitation(false);
    }
  }, [application, callAdminApproveEdgeFunction, showToast]);

  // Load data on mount and when ID changes
  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  // Load token details after application loads (for approved applications)
  // Uses ref to track attempts per application - prevents infinite loop on failure
  useEffect(() => {
    // Reset ref when applicationId changes (navigating to different application)
    if (hasAttemptedTokenLoad.current !== applicationId) {
      hasAttemptedTokenLoad.current = null;
    }

    // Only attempt load if: approved, auth ready, not already loading, not already loaded, not already attempted
    if (
      application &&
      application.status === 'approved' &&
      applicationId &&
      authState.sessionToken &&
      !invitationToken &&
      !loadingToken &&
      hasAttemptedTokenLoad.current !== applicationId
    ) {
      hasAttemptedTokenLoad.current = applicationId;
      loadTokenDetails(applicationId);
    }
  }, [application, applicationId, authState.sessionToken, invitationToken, loadingToken, loadTokenDetails]);

  // Status chip component (reused from BetaApplicationList)
  const StatusBadge: React.FC<{ status: BetaApplicationStatus }> = ({ status }) => {
    const variants = {
      pending: { color: 'warning' as const, text: 'Pending Review' },
      approved: { color: 'success' as const, text: 'Approved' },
      waitlisted: { color: 'secondary' as const, text: 'Waitlisted' },
      rejected: { color: 'danger' as const, text: 'Rejected' },
      activated: { color: 'primary' as const, text: 'Activated' }
    };

    const variant = variants[status];
    return (
      <Chip color={variant.color} variant="flat" size="lg">
        {variant.text}
      </Chip>
    );
  };

  // Format date helper
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle back navigation
  const handleBack = (): void => {
    navigate('/admin/beta-applications');
  };

  // Handle admin decision submission
  const handleDecisionSubmit = async (decision: AdminDecisionData): Promise<void> => {
    if (!application) return;

    try {
      setIsSubmittingDecision(true);

      if (!adminUsername || adminUsername === 'Unknown Admin') {
        throw new Error('Admin user not authenticated');
      }

      // Call Edge Function to handle entire workflow atomically
      const result = await callAdminApproveEdgeFunction(
        application.id,
        decision.status,
        decision.notes || undefined,
        decision.sendEmail,
        decision.sendInvitationEmail
      );

      // Update token state from response (if approved and token created)
      if (result.invitationToken && result.tokenExpiresAt) {
        setInvitationToken({
          token: result.invitationToken,
          expiresAt: result.tokenExpiresAt,
          usedAt: null
        });
      }

      // Reload application data to reflect status change
      await loadApplication();

      // Close modal
      closeDecisionModal();

      // Build success message based on email results
      const emailParts: string[] = [];

      if (decision.sendEmail) {
        if (result.emails.notificationSent) {
          emailParts.push('notification email sent');
        } else if (result.emails.notificationError) {
          emailParts.push('notification email failed');
        }
      }

      if (decision.status === 'approved' && decision.sendInvitationEmail) {
        if (result.emails.invitationSent) {
          emailParts.push('invitation email sent');
        } else if (result.emails.invitationError) {
          emailParts.push('invitation email failed');
        }
      }

      const emailSummary = emailParts.length > 0 ? ` (${emailParts.join(', ')})` : '';

      showToast({
        color: 'success',
        title: 'Decision Submitted',
        description: `Application ${decision.status} successfully${emailSummary}.`
      });

      // Show warning toasts for email failures
      if (decision.sendEmail && result.emails.notificationError) {
        showToast({
          color: 'warning',
          title: 'Notification Email Failed',
          description: result.emails.notificationError
        });
      }

      if (decision.status === 'approved' && decision.sendInvitationEmail && result.emails.invitationError) {
        showToast({
          color: 'warning',
          title: 'Invitation Email Failed',
          description: result.emails.invitationError
        });
      }

    } catch (err) {
      console.error('Failed to submit admin decision:', err);
      showToast({
        color: 'danger',
        title: 'Submission Failed',
        description: err instanceof Error ? err.message : 'Failed to submit decision. Please try again.'
      });
      throw err; // Re-throw to let modal handle the error state
    } finally {
      setIsSubmittingDecision(false);
    }
  };

  // Get email status display
  const getEmailStatusBadge = (): React.ReactNode => {
    if (!application) return null;

    // TODO: Update to use communications table in Phase 3
    // For now, show placeholder status
    return (
      <div className="flex items-center gap-2">
        <Mail size={16} className="text-default-400" />
        <div>
          <p className="text-sm font-medium text-default-600">
            Email Status
          </p>
          <p className="text-xs text-default-500">
            Communication tracking will be implemented in Phase 3
          </p>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout title="Loading Application...">
        <div className="flex items-center justify-center py-12">
          <Spinner color="primary" size="lg" />
          <span className="ml-3 text-text-secondary">Loading application details...</span>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error || !application) {
    return (
      <AdminLayout title="Application Not Found">
        <div className="text-center py-12">
          <p className="text-danger mb-4">{error || 'Application not found'}</p>
          <div className="space-x-4">
            <Button color="primary" onPress={loadApplication}>
              Retry
            </Button>
            <Button variant="flat" onPress={handleBack}>
              Back to List
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`${application.fullName} - Beta Application`}>
      <Card shadow='none' radius="none" fullWidth>
        {/* Header with Back Button and Status */}
        <CardHeader className="flex-shrink-0 flex items-center justify-between flex-wrap gap-4 border-b border-default-200">
          <Button
            variant="flat"
            startContent={<ArrowLeft size={16} />}
            onPress={handleBack}
            className="flex-shrink-0"
          >
            <span className="hidden sm:inline">Back to Applications</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center gap-3">
            <Chip variant="bordered" size="sm" color="default">
            ID: {application.id.slice(-8)}
            </Chip>
            <StatusBadge status={application.status} />
          </div>
        </CardHeader>

        {/* Scrollable Content Section */}
        <CardBody className="space-y-6 p-6">
          <Card shadow="sm" className='flex-shrink-0 border border-default-300'>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User size={18} className="text-primary" />
                <h3 className="text-lg font-semibold text-text-primary">Personal Information</h3>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Full Name</p>
                  <p className="text-text-primary">{application.fullName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Email Address</p>
                  <p className="text-text-primary">{application.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Discord Username</p>
                  <p className="text-text-primary">{application.discordUsername}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Submitted</p>
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-text-secondary" />
                    <p className="text-text-primary">{formatDate(application.createdAt)}</p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Trading Profile Section */}
          <Card shadow="sm" className='flex-shrink-0 border border-default-300'>
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp size={18} className="text-primary" />
                <h3 className="text-lg font-semibold text-text-primary">Trading Profile</h3>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-2">Primary Trading Focus</p>
                  <div className="flex flex-wrap gap-2">
                    {application.primaryFocus.map((focus, index) => (
                      <Chip key={index} size="sm" variant="flat" color="primary">
                        {focus}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Experience Level</p>
                  <Chip variant="bordered" color="secondary">
                    {application.experienceLevel}
                  </Chip>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Trading Style</p>
                  <p className="text-text-primary">
                    {application.tradingStyle === 'other'
                      ? application.tradingStyleOther || 'Other'
                      : application.tradingStyle
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Account Type</p>
                  <Chip variant="flat" color="default">
                    {application.accountType}
                  </Chip>
                </div>
                {application.propFirm && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-text-secondary mb-1">Prop Firm</p>
                      <p className="text-text-primary">{application.propFirm}</p>
                    </div>
                    {application.propFirmStatus && (
                      <div>
                        <p className="text-sm font-medium text-text-secondary mb-1">Prop Firm Status</p>
                        <Chip variant="flat" size="sm" color="warning">
                          {application.propFirmStatus}
                        </Chip>
                      </div>
                    )}
                    {application.propFirmPayoutHistory && (
                      <div>
                        <p className="text-sm font-medium text-text-secondary mb-1">Payout History</p>
                        <Chip variant="flat" size="sm" color="success">
                          {application.propFirmPayoutHistory}
                        </Chip>
                      </div>
                    )}
                  </>
                )}
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Broker</p>
                  <p className="text-text-primary">{application.broker}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-2">Trading Sessions</p>
                  <div className="flex flex-wrap gap-2">
                    {application.tradingSessions.map((session, index) => (
                      <Chip key={index} size="sm" variant="bordered">
                        {session === 'other'
                          ? application.tradingSessionsOther || 'Other'
                          : session
                        }
                      </Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Current Journaling Method</p>
                  <p className="text-text-primary">{application.currentJournaling}</p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Beta Commitment Section */}
          <Card shadow="sm" className='flex-shrink-0 sborder border-default-300'>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Target size={18} className="text-primary" />
                <h3 className="text-lg font-semibold text-text-primary">Beta Commitment</h3>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div>
                <p className="text-sm font-medium text-text-secondary mb-2">Interest in {Brand.displayName} Beta</p>
                <p className="text-text-primary">{application.stjInterest}</p>
              </div>
              <Divider />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Testing Commitment</p>
                  <Chip variant="flat" color="primary">
                    {application.testingCommitment}
                  </Chip>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Feedback Commitment</p>
                  <Chip variant="flat" color="secondary">
                    {application.feedbackCommitment}
                  </Chip>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Invitation Token Section - Only for approved applications */}
          {application.status === 'approved' && (
            <Card shadow="sm" className='flex-shrink-0 border border-default-300'>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Mail size={18} className="text-primary" />
                  <h3 className="text-lg font-semibold text-text-primary">Invitation Status</h3>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                {invitationToken ? (
                  <>
                    <div className="bg-primary-50 dark:bg-primary-900/10 p-4 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-text-secondary">Status:</span>
                        <Chip
                          color={invitationToken.usedAt ? "success" : "warning"}
                          variant="flat"
                          size="sm"
                        >
                          {invitationToken.usedAt ? "Activated" : "Pending"}
                        </Chip>
                      </div>
                      <Divider />
                      <div>
                        <span className="text-sm font-medium text-text-secondary">Expires:</span>
                        <p className="text-sm text-text-primary mt-1">
                          {new Date(invitationToken.expiresAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      {invitationToken.usedAt && (
                        <>
                          <Divider />
                          <div>
                            <span className="text-sm font-medium text-text-secondary">Activated:</span>
                            <p className="text-sm text-text-primary mt-1">
                              {new Date(invitationToken.usedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </>
                      )}
                      {!invitationToken.usedAt && (
                        <>
                          <Divider />
                          <div>
                            <span className="text-sm font-medium text-text-secondary">Activation Link:</span>
                            <p className="text-xs text-text-primary mt-1 break-all font-mono bg-default-100 p-2 rounded">
                              {Brand.appUrl}/activate/{invitationToken.token}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                    {!invitationToken.usedAt && (
                      <Button
                        color="primary"
                        variant="flat"
                        size="sm"
                        startContent={<Mail size={16} />}
                        onPress={handleResendInvitation}
                        isLoading={isResendingInvitation}
                      >
                        Resend Invitation Email
                      </Button>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-text-secondary">No invitation token found</p>
                    <p className="text-xs text-text-secondary mt-1">Token may have expired or not been generated</p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </CardBody>
        <CardFooter className="flex-shrink-0 border-t border-default-400">
          {/* Admin Status Section - Collapsible */}
          <Accordion
            variant="light"
            fullWidth
            isCompact
            selectedKeys={expandedAccordionKeys}
            onSelectionChange={(keys) => setExpandedAccordionKeys(new Set(Array.from(keys as Set<string>)))}
          >
            <AccordionItem
              key="admin-review"
              aria-label="Admin Review Status"
              title={
                <div className="flex items-center gap-2 pb-2 border-b-1 border-default-100">
                  <Shield size={18} className="text-primary" />
                  <span className="text-lg font-semibold text-primary-800 dark:text-primary-200">
                    Admin Review
                  </span>
                </div>
              }
            >
              <div className="space-y-4 pb-4 sm:max-h-none max-h-[30vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-1">
                      Current Status
                    </p>
                    <StatusBadge status={application.status} />
                  </div>
                  {application.reviewedBy && (
                    <div>
                      <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-1">
                        Reviewed By
                      </p>
                      <p className="text-primary-800 dark:text-primary-200">{application.reviewedBy}</p>
                    </div>
                  )}
                  {application.reviewedAt && (
                    <div>
                      <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-1">
                        Review Date
                      </p>
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-primary-600 dark:text-primary-400" />
                        <p className="text-primary-800 dark:text-primary-200">
                          {formatDate(application.reviewedAt)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {application.adminNotes && (
                  <>
                    <Divider />
                    <div>
                      <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                        Admin Notes
                      </p>
                      <p className="text-primary-800 dark:text-primary-200 bg-primary-100 dark:bg-primary-800/20 p-3 rounded-md">
                        {application.adminNotes}
                      </p>
                    </div>
                  </>
                )}
                {/* Email Status Display */}
                {getEmailStatusBadge() && (
                  <>
                    <Divider />
                    <div>
                      <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                        Email Status
                      </p>
                      {getEmailStatusBadge()}
                    </div>
                  </>
                )}

                {/* Admin Action Button */}
                <div className="flex justify-end pt-4 border-t border-default-200">
                  <Button
                    color={application.status === 'pending' ? 'primary' : 'secondary'}
                    variant="solid"
                    size="sm"
                    onPress={openDecisionModal}
                    startContent={<UserCheck size={16} />}
                    className="flex-shrink-0"
                  >
                    <span className="hidden sm:inline">
                      {application.status === 'pending' ? 'Review Application' : 'Change Status'}
                    </span>
                    <span className="sm:hidden">
                      {application.status === 'pending' ? 'Review' : 'Update'}
                    </span>
                  </Button>
                </div>
              </div>
            </AccordionItem>
          </Accordion>
        </CardFooter>
      </Card>

      {/* Admin Decision Modal */}
      {application && (
        <AdminDecisionModal
          isOpen={isDecisionModalOpen}
          onClose={closeDecisionModal}
          application={application}
          onSubmit={handleDecisionSubmit}
          isSubmitting={isSubmittingDecision}
        />
      )}
    </AdminLayout>
  );
};